import { WritableSignal } from '@angular/core';
import { FormGroup } from '@angular/forms';

/*
 * Collects and returns all validation errors from a FormGroup, including nested FormGroups.
 * @param group The FormGroup to collect errors from.
 * @param messages Optional custom error messages for each control.
 * @returns A record of control names and their corresponding error messages.
 */
export function collectFormValidationErrors(
  group: FormGroup,
  messages: Record<string, { pattern?: string; required?: string; min?: string }> = {},
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [key, control] of Object.entries(group.controls)) {
    if (control instanceof FormGroup) {
      Object.assign(errors, collectFormValidationErrors(control, messages));
      continue;
    }
    if (control.invalid) {
      const msg = messages[key];
      if (control.hasError('pattern')) {
        errors[key] = msg?.pattern ?? 'Invalid format';
      } else if (control.hasError('min')) {
        errors[key] = msg?.min ?? 'Minimum value required';
      } else {
        errors[key] = msg?.required ?? 'Required';
      }
    }
  }
  return errors;
}

// Clears a specific form validation error from the provided WritableSignal and leaves the rest
export function clearFormValidationError(errorsSignal: WritableSignal<Record<string, string>>, key: string): void {
  errorsSignal.update((errors) => {
    if (!(key in errors)) return errors;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: _, ...rest } = errors;
    return rest;
  });
}

// If the variant is 'editable', returns a boolean indicating if the field is invalid as form validation will highlight it.
// If the variant is 'view', returns a CSS class string for styling invalid fields or null if valid.
export function checkFieldInvalid(
  invalidFields: readonly string[] | undefined,
  fieldName: string,
  variant: 'view' | 'editable',
): string | boolean | null {
  const hasMatch = invalidFields?.includes(fieldName) ?? false;
  if (variant === 'editable') return hasMatch;
  return hasMatch ? 'rounded-md border-2 border-error py-2 pl-3' : null;
}

export const YES_NO = { YES: 'Yes', NO: 'No' } as const;
export type YesNo = 'Yes' | 'No';

// Returns undefined when unset so hsc-form-field hides the read-only row.
export const boolToYesNo = (v: boolean | null | undefined): YesNo | undefined => {
  if (v == null) return undefined;
  return v ? YES_NO.YES : YES_NO.NO;
};
